/**
 * FormGroup Component is a custom component which renders input fields based on the JSON.
 * 
 */

import { FormControl, Grid, TextField, Select, MenuItem, Button, Checkbox, FormControlLabel, Box } from '@mui/material';
import React, { useState, useImperativeHandle } from 'react';
import { makeStyles } from "@mui/styles";
import TagsInput from './requestdetails/TagsInput';
import PermissionButton from './PermissionButton';
import { useRolePermissions } from '../../common/RolePermissions';

const useStyles = makeStyles({
    root: {
        '& .ml-1': {
            fontWeight: 'bold',
            width: '100%'
        },
        '& .MuiInputBase-input': {
            paddingTop: '5px',
            paddingBottom: '5px',
            fontSize: '0.8rem',
            paddingLeft: '10px'
        },
        '& .MuiListItem-root': {
            fontSize: '0.85rem',
            padding: '0px'
        }
    },
    menuitemmultiselect: {
        fontSize: '0.85rem',
        padding: '8px 12px',
        '& .Mui-checked': {
            color: '#7f9ed7'
        }
    },
    menuitemmultiselectNoCheck: {
        paddingTop: '10px',
        paddingBottom: '10px',
        paddingLeft: '16px'
    },
    disabledMenuItem: {
        pointerEvents: 'none',
    },
    actionButton: {
        fontSize: '0.75rem',
        padding: '4px',
        margin: '0px 10px 0px'
    },
    formActionWrapper: {
        marginBottom: '0px'
    }
});


function FormGroup({
    fields,
    disableFormActions = false,
    customFormUpdates,
    refreshGridWithSearch,
    formWrapperClassName,
    formError = "",
    handleClearForm,
    defaultFormData,
    cancelButtonText = "Cancel",
    submitButtonText = "Submit",
    submitPermission,
}, ref) {
    const { hasPermission } = useRolePermissions();
    const [resetField, setResetField]=useState(0);
    const [inputValue, setInputValue] = React.useState("");
    // useImperativeHandle - This is used to allow access to internal Functionality usiing the reference
    useImperativeHandle(ref, () => {
        // Exposing only the Refresh Form so that user can reset By calling this method
        return {
            resetFormToDefaults,
            handleFormDataState
        }
    });

    const getDefaultValues = (defaultFormData = {}) => {
        const defaultValues = defaultFormData;
        fields.map((field) => {
            let defaultValue = field.defaultOption;
            if (field.isMulti && field.defaultOption) {
                defaultValue = [field.defaultOption];
            }
            defaultValues[field.name] = defaultValues[field.name] || defaultValue;
            return field;
        })
        return defaultValues;
    }

    const defaultValues = getDefaultValues(defaultFormData);
    const classes = useStyles();
    const [formData, setFormData] = useState(defaultValues);

    const resetFormToDefaults = () => {
        setFormData({
            ...defaultValues
        });
        setResetField(resetField+1);
        setInputValue("");
    }
    const handleFormDataState = () => {
        setFormData(getDefaultValues());
    }
      const getEnteredGuid =(formData)=>{
     if (inputValue) {
      const newSelectedItem = Array.isArray(formData.guids) ? [...formData.guids]:[];
      const duplicatedValues = newSelectedItem.indexOf(
        inputValue.trim()
      );

      if(duplicatedValues === -1){
      newSelectedItem.push(inputValue.trim());
      }
     return newSelectedItem
    }else{
     return formData.guids
    }
  }
    const handleSubmitForm = () => {
        if (!hasPermission(submitPermission)) return;
        // Merge the typed-but-not-committed GUID for this submit only, so clearing the input clears the value.
        const submittedFormData = inputValue ? { ...formData, guids: getEnteredGuid(formData) } : formData;

        refreshGridWithSearch(submittedFormData);
    }
    const updateFormData = (field, value) => {
        if (field.isMulti) {
            const existingSelection = formData[field.name] || [];
            const newSelection = value.filter(v => !existingSelection.includes(v));
            if (newSelection.includes("All")) {
                value = ["All"];
            } else if (existingSelection.includes("All") && newSelection.length > 0) {
                // Expand "All" to all individual options, then remove the clicked option
                const allOptions = (field.displayOption ? field.options.map(o => o.id) : field.options).filter(o => o !== "All");
                value = allOptions.filter(o => !newSelection.includes(o));
            }
            // Auto-select "All" when all individual options are selected
            const allOptions = (field.displayOption ? field.options.map(o => o.id) : field.options).filter(o => o !== "All");
            const selectedNonAll = value.filter(v => v !== "All");
            if (allOptions.length > 0 && selectedNonAll.length === allOptions.length) {
                value = ["All"];
            }
        }
        const shouldResetForm = field.resetFormOnChange && formData[field.name] !== value;
        const preservedValues = {};
        if (shouldResetForm) {
            (field.preserveFieldsOnReset || []).forEach((name) => {
                preservedValues[name] = formData[name];
            });
        }
        const updatedFormData = {
            ...(shouldResetForm ? { ...getDefaultValues(), ...preservedValues } : formData),
            [field.name]: value
        };
        setFormData(updatedFormData);
        if (shouldResetForm) {
            setResetField(previousResetField => previousResetField + 1);
            setInputValue("");
            handleClearForm?.();
        }
        if (disableFormActions) {
            customFormUpdates(updatedFormData);
        }
    }
    const isDisabled = (name, fieldObj) => {
        return false;
    }
    const renderCheckBox = (field) => {
        return (
            <Grid item xs={field.size || 3} key={'CheckBoxField' + field.name} className={`${field.customInputCls}`}>
                <div>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData[field.name]}
                                onChange={(event) => { updateFormData(field, event.target.checked) }}
                                name={field.name}
                                color="primary"
                            />
                        }
                        label={field.label}
                    />
                </div>
            </Grid>
        )
    }

    return (
        <Grid container className={`${classes.root} ${formWrapperClassName}`}>
            {fields.map((field) => {
                if(field.hiddenCallback){
                    field.hidden = field.hiddenCallback(formData)
                }
                if(field.disabledCallback){
                    field.isDisabled = field.disabledCallback(formData)
                }
                if(field.requiredCallback){
                    field.required = field.requiredCallback(formData)
                }
                if (field.type === "text" || field.type === "number") {
                    if (field.hidden === true) return null;
                    const additionalProps = field.additionalProps || {};
                    if (field.handleEnterToSubmit) {
                        additionalProps["onKeyDown"] = (e) => {
                            if (e?.key === "Enter") {
                                handleSubmitForm();
                            }
                        }
                    }
                    return (
                        <Grid item xs={field.size || 3} key={"textField" + field.name}>
                            <div className="form-group col">
                                <label htmlFor={field.name} className="ml-1">{field.label}{field.required && (<span style={{color:"red"}}> *</span>)}</label>
                                <TextField
                                    inputProps={{maxLength:field.maxLength}}
                                    disabled={field.isDisabled}
                                    size={"small"}
                                    data-testid={"textField-" + field.name}
                                    type={field.type}
                                    onChange={(event) => { updateFormData(field, event.target.value) }}
                                    value={(formData[field.name] || [field.defaultOption])}
                                    fullWidth
                                    id={field.name}
                                    label=""
                                    variant="outlined"
                                    required
                                    {...additionalProps}
                                />
                            </div>
                        </Grid>
                    )
                } else if (field.type === "multiselect" && field.hidden !== true) {
                    return (
                        <Grid item xs={field.size || 3} key={"multiselect-" + field.label} >
                            <div className="form-group col">
                                {field.label && (<label htmlFor={field.name} className="ml-1">{field.label} {field.required && (<span style={{color:"red"}}>*</span>)}</label>)}
                                <FormControl fullWidth className={`${field.customInputCls}`} variant="outlined" >
                                    <Select
                                        size={"small"}
                                        id={field.name}
                                        multiple={field.isMulti}
                                        data-testid={"selectField-" + field.name}
                                        onChange={(event) => { if(field.isMulti){updateFormData(field, event.target.value)} }}
                                        value={function () { return formData[field.name] || (field.displayOption ? field.defaultOption : [field.defaultOption]) }()}
                                        renderValue={(selected) => {
                                            if (field.displayOption) {
                                                let displaySelectedValue = '';
                                                field.options.forEach((_option) => {
                                                    if (_option.id === selected) {
                                                        displaySelectedValue = _option[field.displayOption]
                                                    }
                                                })
                                                return displaySelectedValue;
                                            }
                                            return field.isMulti ? selected?.join?.(', ') : selected
                                        }}
                                    >
                                        {field.displayOption ? (field.options.map((_option) => (
                                            <MenuItem key={_option.id} className={`${classes.menuitemmultiselect} ${field.removeCheckBox && classes.menuitemmultiselectNoCheck} ${(isDisabled(_option.id, field) && classes.disabledMenuItem)}`} value={_option.id}>
                                                {!field.removeCheckBox && <Checkbox disabled={isDisabled(_option.id, field)} checked={formData[field.name]?.includes("All") || formData[field.name]?.indexOf(_option.id) > -1} />}
                                                {_option[field.displayOption]}
                                            </MenuItem>
                                        ))) :
                                            (field.options.map((name) => (
                                                <MenuItem 
                                                    key={name} 
                                                    className={`${classes.menuitemmultiselect} ${field.removeCheckBox && classes.menuitemmultiselectNoCheck} ${(isDisabled(name, field) && classes.disabledMenuItem)}`} 
                                                    value={name}
                                                    onClick={(e)=>{
                                                        
                                                        if(!field.isMulti){
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            const currentValue =formData[field.name];
                                                            const selectedItem =  name;
                                                            const newValue = currentValue ===selectedItem ? '': selectedItem;
                                                            updateFormData(field, newValue);
                                                        }
                                                    }}
                                                >
                                                    {!field.removeCheckBox && <Checkbox disabled={isDisabled(name, field)} checked={field.isMulti ? (formData[field.name]?.includes("All") || formData[field.name]?.indexOf(name) > -1) : formData[field.name]===name } />}
                                                    {name}
                                                </MenuItem>
                                            )))}
                                    </Select>
                                </FormControl>
                                {field.isMulti && formData[field.name]?.length > 1 && (<p className="multiSelectedOptions"> {formData[field.name].join(' , ')}</p>)}
                            </div>
                        </Grid>
                    )
                } else if (field.type === "chips" && field.hidden !== true) {
                    return (
                        <Grid item xs={field.size || 3} key={"multiselect-" + field.label} >
                            <div className="form-group col">
                                {field.label && (<label htmlFor={field.name} className="ml-1">{field.label}{field.required && (<span style={{color:"red"}}> *</span>)}</label>)}
                                <FormControl fullWidth className={`${field.customInputCls}`} variant="outlined" >
                                    <TagsInput 
                                    value={formData[field.name] || []} 
                                    selectedTags={(event) => { updateFormData(field, event) }} 
                                    resetField={resetField}
                                    inputValue={inputValue} 
                                    setInputValue={setInputValue} />
                                </FormControl>
                                {field.isMulti && formData[field.name]?.length > 1 && (<p className="multiSelectedOptions"> {formData[field.name].join(' , ')}</p>)}
                            </div>
                        </Grid>
                    )
                } else if ((field.type === "date" || field.type === "month") && !field.isHidden) {
                    return (
                        <Grid item xs={3} key={'dateField' + field.name}>
                            <div className="form-group col">
                                <label htmlFor={field.name} className="ml-1">{field.label}</label>
                                <TextField variant="outlined"
                                    disabled={field.isDisabled}
                                    id={field.name}
                                    inputProps={{
                                        size: "small",
                                        ...field?.inputAdditionalProps
                                    }}
                                    label=""
                                    type={field.type}
                                    onChange={(event) => { updateFormData(field, event.target.value) }}
                                    value={(formData[field.name] || [field.defaultOption])}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </div>
                        </Grid>
                    )
                } else if (field.type === "checkbox" && field.hidden !== true) {
                    return renderCheckBox(field)
                }
                return null;
            })}
            {formError !== "" && (<Box color={"red"}>
                {formError}
            </Box>)}
            {!disableFormActions && <Grid container item className={classes.formActionWrapper} spacing={4} xs={12} justifyContent={"center"}>
                <Grid item >
                    <Button className={classes.actionButton} onClick={resetFormToDefaults} variant="contained" color="primary"
                     data-testid={"formgroupcancel"}
                    >
                        {cancelButtonText}
                    </Button>
                </Grid>
                <Grid item >
                    <PermissionButton
                        permission={submitPermission}
                        className={classes.actionButton}
                        onClick={handleSubmitForm}
                        variant="contained"
                        color="primary"
                        data-testid={"formgroupsubmit"} >
                        {submitButtonText}
                    </PermissionButton>
                </Grid>
            </Grid>}
        </Grid>);
}
// Wrapping component with forwarRef so that reference functionality works
export default React.forwardRef(FormGroup);